import { __awaiter, __generator } from '../../_virtual/_tslib.js';
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick } from '@vue/composition-api';
import { respondToBelow, ScreenWidth } from '@superbet-group/web.lib.vue-utilities';
import __vue_component__ from '../SdsButtonIcon/SdsButtonIcon.vue.js';
import __vue_component__$1 from '../SdsIcon/SdsIcon.vue.js';
import __vue_component__$2 from '../SdsOverlaySelect/SdsOverlaySelect.vue.js';
import __vue_component__$3 from '../SdsDrawerSelect/SdsDrawerSelect.vue.js';
import { isPointInsideRect } from '../utils/isPointInsideRect.js';

var script = defineComponent({
    name: 'SdsSelect',
    props: {
        size: {
            type: String,
            required: false,
            default: 'lg',
        },
        label: {
            type: String,
            required: true,
        },
        useLabelAsDisplayValue: {
            type: Boolean,
            required: false,
            default: false,
        },
        selectedOption: {
            type: String,
            required: false,
            default: undefined,
        },
        options: {
            type: Array,
            required: true,
        },
        areOptionsLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        name: {
            type: String,
            required: true,
        },
        isDisabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        message: {
            type: String,
            required: false,
            default: '',
        },
        isCheckmarkEnabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        isRequired: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['update:selectedOption', 'click:toggleOptions'],
    setup: function (props, _a) {
        var emit = _a.emit;
        var selectField = ref(null);
        var dropdown = ref(null);
        var selectedItem = ref(null);
        var selectedOptionLabel = computed(function () { var _a; return (_a = props.options.find(function (o) { return o.value === props.selectedOption; })) === null || _a === void 0 ? void 0 : _a.name; });
        var isTouched = ref(!!props.selectedOption);
        var isFocused = ref(false);
        var isInvalid = computed(function () { return props.isRequired && ((!!props.message || !props.selectedOption) && isTouched.value && !isFocused.value); });
        var isExpanded = ref(false);
        var hasAssignedValue = computed(function () { return !!props.selectedOption; });
        var isCheckmarkDisplayed = computed(function () { return !isFocused.value && props.isCheckmarkEnabled && hasAssignedValue.value; });
        var displayValue = computed(function () {
            if (props.useLabelAsDisplayValue) {
                return props.label;
            }
            // for size lg it is a selected option, while label is a separate animated element above it
            // for size sm we have the label we pass or a selected option interchanging in the same element
            return props.size === 'lg' ? selectedOptionLabel.value : selectedOptionLabel.value || props.label;
        });
        var stateIcon = computed(function () {
            if (isInvalid.value) {
                return 'status-danger-filled';
            }
            else if (!isFocused.value && isCheckmarkDisplayed.value) {
                return 'status-checkmark-alt-filled';
            }
            else {
                return '';
            }
        });
        function toggleOptions(event) {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            emit('click:toggleOptions');
                            if (props.isDisabled) {
                                return [2 /*return*/];
                            }
                            if (isExpanded.value) {
                                isTouched.value = true;
                            }
                            event.stopPropagation();
                            isExpanded.value = !isExpanded.value;
                            return [4 /*yield*/, nextTick()];
                        case 1:
                            _a.sent();
                            return [2 /*return*/];
                    }
                });
            });
        }
        function closeDropdownOnClickOutside(event) {
            var _a, _b, _c;
            var domRects = [
                (_a = dropdown.value) === null || _a === void 0 ? void 0 : _a.$el,
                selectField.value,
                document.querySelector('.e2e-dropdown-input-modal'),
            ].filter(function (it) { return it != null; }).map(function (it) { return it.getBoundingClientRect(); });
            var point;
            if (event instanceof MouseEvent) {
                point = new DOMPoint(event.clientX, event.clientY);
            }
            else if (event instanceof TouchEvent && event.touches.length > 0) {
                point = new DOMPoint((_b = event.touches[0]) === null || _b === void 0 ? void 0 : _b.clientX, (_c = event.touches[0]) === null || _c === void 0 ? void 0 : _c.clientY);
            }
            else {
                return;
            }
            if (domRects.every(function (domRect) { return domRect && !isPointInsideRect(point, domRect); })) {
                isExpanded.value = false;
            }
        }
        function passFocusToSelectField() {
            var _a;
            (_a = selectField.value) === null || _a === void 0 ? void 0 : _a.focus();
            isExpanded.value = !isExpanded.value;
        }
        function selectOption(option) {
            if (option.value === props.selectedOption) {
                emit('update:selectedOption', null);
            }
            else {
                emit('update:selectedOption', option.value);
                isExpanded.value = false;
            }
        }
        function closeWithEsc(event) {
            if (isExpanded.value) {
                toggleOptions(event);
            }
        }
        onMounted(function () {
            window.addEventListener('mousedown', closeDropdownOnClickOutside);
            window.addEventListener('touchstart', closeDropdownOnClickOutside);
        });
        onBeforeUnmount(function () {
            window.removeEventListener('mousedown', closeDropdownOnClickOutside);
            window.removeEventListener('touchstart', closeDropdownOnClickOutside);
        });
        return {
            dropdown: dropdown,
            selectField: selectField,
            selectedItem: selectedItem,
            selectedOptionLabel: selectedOptionLabel,
            stateIcon: stateIcon,
            isInvalid: isInvalid,
            isTouched: isTouched,
            isFocused: isFocused,
            isExpanded: isExpanded,
            isErrorIcon: computed(function () { return !isFocused.value && isInvalid.value; }),
            isCheckmarkIcon: computed(function () { return !isFocused.value && !isInvalid.value; }),
            isMobileLayout: computed(function () { return respondToBelow(ScreenWidth.tLg); }),
            displayValue: displayValue,
            passFocusToSelectField: passFocusToSelectField,
            toggleOptions: toggleOptions,
            selectOption: selectOption,
            closeWithEsc: closeWithEsc,
        };
    },
    components: {
        SdsButtonIcon: __vue_component__,
        SdsIcon: __vue_component__$1,
        SdsOverlaySelect: __vue_component__$2,
        SdsDrawerSelect: __vue_component__$3,
    },
});

export default script;
